<template>
    <div class="card p-fluid">
        <div class="timeframe-selector mb-3">
            <h5>Select Timeframe</h5>
            <div class="timeframe-buttons">
                <div class="button-group">
                    <h6>Previous</h6>
                    <Button :class="{ 'p-button-outlined': selectedTimeframe !== 'prev-week' }"
                        @click="handleSetTimeframe('prev-week')">Week</Button>
                    <Button :class="{ 'p-button-outlined': selectedTimeframe !== 'prev-month' }"
                        @click="handleSetTimeframe('prev-month')">Month</Button>
                    <Button :class="{ 'p-button-outlined': selectedTimeframe !== 'prev-quarter' }"
                        @click="handleSetTimeframe('prev-quarter')">Quarter</Button>
                </div>
                <div class="button-group">
                    <h6>Current</h6>
                    <Button :class="{ 'p-button-outlined': selectedTimeframe !== 'current-week' }"
                        @click="handleSetTimeframe('current-week')">Week</Button>
                    <Button :class="{ 'p-button-outlined': selectedTimeframe !== 'current-month' }"
                        @click="handleSetTimeframe('current-month')">Month</Button>
                    <Button :class="{ 'p-button-outlined': selectedTimeframe !== 'current-quarter' }"
                        @click="handleSetTimeframe('current-quarter')">Quarter</Button>
                </div>
            </div>
        </div>

        <div class="calender-wrapper">
            <div class="calendar-container">
                <Calendar v-model="localDates" selectionMode="range" :manualInput="false"
                    :date-format="dd / MM / yyyy" showButtonBar @update:modelValue="handleUpdateDates" />
            </div>
            <Button :disabled="!localDates || localDates?.length == 0 || localDates[0] == null || localDates[1] == null"
                @click="handleGetInsights" :loading="loading">Get Data</Button>
        </div>

        <div v-if="loading" class="loader">
            <p>Loading...</p>
        </div>

        <template v-else>
            <DataTable :value="summaryData" showGridlines class="mb-4" responsiveLayout="scroll">
                <Column header="RFP Action Required" field="rfpActionRequired">
                    <template #body="{ data }">
                        <template v-if="data.rfpActionRequired > 0">
                            <a href="#rfp-action-required" @click.prevent="handleScrollToSection('rfp-action-required')" class="summary-link">{{ data.rfpActionRequired }}</a>
                        </template>
                        <template v-else>
                            {{ data.rfpActionRequired }}
                        </template>
                    </template>
                </Column>
                <Column header="Invites Received" field="invitesReceived">
                    <template #body="{ data }">
                        <template v-if="data.invitesReceived > 0">
                            <a href="#invites-received" @click.prevent="handleScrollToSection('invites-received')" class="summary-link">{{ data.invitesReceived }}</a>
                        </template>
                        <template v-else>
                            {{ data.invitesReceived }}
                        </template>
                    </template>
                </Column>
                <Column header="RFPs Declined" field="rfpsDeclined">
                    <template #body="{ data }">
                        <template v-if="data.rfpsDeclined > 0">
                            <a href="#rfps-declined" @click.prevent="handleScrollToSection('rfps-declined')" class="summary-link">{{ data.rfpsDeclined }}</a>
                        </template>
                        <template v-else>
                            {{ data.rfpsDeclined }}
                        </template>
                    </template>
                </Column>
                <Column header="RFPs Not Responded To" field="rfpsNotResponded">
                    <template #body="{ data }">
                        <template v-if="data.rfpsNotResponded > 0">
                            <a href="#rfps-not-responded-to" @click.prevent="handleScrollToSection('rfps-not-responded-to')" class="summary-link">{{ data.rfpsNotResponded }}</a>
                        </template>
                        <template v-else>
                            {{ data.rfpsNotResponded }}
                        </template>
                    </template>
                </Column>
                <Column header="RFPs Participated In" field="rfpsParticipated">
                    <template #body="{ data }">
                        <template v-if="data.rfpsParticipated > 0">
                            <a href="#rfps-participated-in" @click.prevent="handleScrollToSection('rfps-participated-in')" class="summary-link">{{ data.rfpsParticipated }}</a>
                        </template>
                        <template v-else>
                            {{ data.rfpsParticipated }}
                        </template>
                    </template> 
                </Column>
                <Column header="RFPs Not Awarded" field="rfpsNotAwarded">
                    <template #body="{ data }">
                        <template v-if="data.rfpsNotAwarded > 0">
                            <a href="#rfps-not-awarded" @click.prevent="handleScrollToSection('rfps-not-awarded')" class="summary-link">{{ data.rfpsNotAwarded }}</a> 
                        </template>
                        <template v-else>
                            {{ data.rfpsNotAwarded }}
                        </template> 
                    </template>
                </Column>
                <Column header="RFPs Awarded" field="rfpsAwarded">
                    <template #body="{ data }">
                        <template v-if="data.rfpsAwarded > 0">
                            <a href="#rfps-awarded" @click.prevent="handleScrollToSection('rfps-awarded')" class="summary-link">{{ data.rfpsAwarded }}</a>
                        </template>
                        <template v-else>
                            {{ data.rfpsAwarded }}
                        </template> 
                    </template>
                </Column>
                <Column header="Signed Contracts" field="rfpsSigned">
                    <template #body="{ data }">
                        <template v-if="data.rfpsSigned > 0">
                            <a href="#rfps-signed" @click.prevent="handleScrollToSection('rfps-signed')" class="summary-link">{{ data.rfpsSigned }}</a>
                        </template>
                        <template v-else>
                            {{ data.rfpsSigned }}
                        </template>
                    </template>
                </Column>
            </DataTable>

            <template v-for="(items, status) in groupedRfps" :key="status">
                <div v-if="items.length > 0" class="status-section mb-4" :id="getSectionId(status)">
                    <h5>{{ status }}</h5>
                    <DataTable :value="items" showGridlines :scrollable="true" responsiveLayout="scroll"
                        stripedRows :resizableColumns="true">
                        <template #empty>
                            <div class="empty-state">
                                <i class="pi pi-info-circle text-gray-500 text-xl mb-2"></i>
                                <p class="m-0">No records found for this status</p>
                            </div>
                        </template>
                        <Column field="id" header="RFP ID" sortable>
                            <template #body="{ data }">
                                <div v-if="readOnly" class="opportunityLink-readonly">
                                    {{ data.id }}
                                </div>
                                <div v-else @click="handleNavigateToRfp(data.id, data.encId)" class="opportunityLink">
                                    {{ data.id }}
                                </div>
                            </template>
                        </Column>
                        <Column field="name" header="RFP Title" sortable class="wrap-column"></Column>
                        <Column field="status" header="Status" sortable></Column>
                        <Column field="startDate" header="Start Date" sortable></Column>
                        <Column field="endDate" header="End Date" sortable></Column>
                    </DataTable>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
export default {
    name: 'OpportunitiesContent',
    props: {
        dates: {
            type: Array,
            default: () => null
        },
        selectedTimeframe: {
            type: String,
            default: 'prev-month'
        },
        loading: {
            type: Boolean,
            default: false
        },
        summaryData: {
            type: Array,
            default: () => []
        },
        groupedRfps: {
            type: Object,
            default: () => ({})
        },
        dataAccessCode: {
            type: String,
            default: null
        },
        readOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            localDates: null
        }
    },
    watch: {
        dates: {
            immediate: true,
            handler(newVal) {
                this.localDates = newVal;
            }
        }
    },
    methods: {
        handleSetTimeframe(timeframe) {
            this.$emit('set-timeframe', timeframe);
        },
        handleUpdateDates(newDates) {
            this.localDates = newDates;
            this.$emit('update-dates', newDates);
        },
        handleGetInsights() {
            this.$emit('get-insights');
        },
        handleScrollToSection(sectionId) {
            this.$emit('scroll-to-section', sectionId);
        },
        handleNavigateToRfp(id, encId) {
            this.$emit('navigate-to-rfp', id, encId);
        },
        getSectionId(status) {
            // Convert status to lowercase and replace spaces with hyphens
            const id = status.toLowerCase().trim().replace(/\s+/g, '-');
            return id;
        }
    }
}
</script>

<style lang="scss" scoped>
.opportunityLink {
    color: darkgreen;
    text-decoration: underline;
    cursor: pointer;
}

.opportunityLink-readonly {
    color: #333;
    text-decoration: none;
    cursor: default;
}

.calender-wrapper {
    display: flex;
    margin-top: 10px;
    margin-bottom: 16px;
    gap: 10px;
}

.calendar-container {
    width: 200px;
}

.empty-state {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: center;
}

.timeframe-selector {
    .timeframe-buttons {
        display: flex;
        gap: 2rem;

        .button-group {
            display: flex;
            align-items: center;
            gap: 0.5rem;

            h6 {
                margin: 0;
                min-width: 70px;
            }

            button {
                min-width: 100px;
            }
        }
    }
}

.status-section {
    margin-top: 2rem;

    h5 {
        margin-bottom: 1rem;
    }
}

.wrap-column {
    max-width: 300px;
}

.wrap-text {
    white-space: normal;
    word-wrap: break-word;
    word-break: break-word;
}

.p-button:enabled:focus {
    background: #00796b !important;
}

.loader {
    display: flex;
    justify-content: center;
    margin: 2rem auto;
}

.flexed-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

// Add these new styles
:deep(.p-datatable) {
    .p-datatable-header {
        background-color: #e4edd9 !important;
        font-size: 1.1rem;
        font-weight: bold;
    }

    .p-datatable-thead > tr > th {
        background-color: #e4edd9 !important;
        color: #333;
        font-size: 1.1rem;
        font-weight: bold;
        padding: 1rem;
        border: 1px solid #ddd;
    }

    .p-datatable-tbody > tr > td {
        padding: 0.75rem 1rem;
        border: 1px solid #ddd;
    }

    // Add subtle hover effect on rows
    .p-datatable-tbody > tr:hover {
        background-color: #f8f9fa;
    }

    // Add subtle shadow to the table
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    overflow: hidden;
}

.summary-link {
    color: #00796b;
    text-decoration: underline;
    font-weight: bold;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
        color: #004d40;
        text-decoration: underline;
    }
}
</style>

